import React, { useRef, useState } from 'react'

import styles from '../../SpreadsheetTable/SpreadsheetContainer/_spreadsheet-container.module.scss'
import { SearchBar, Switch } from '../../../../module'
import { SpreadsheetPane } from '../../../SpreadsheetPane/SpreadsheetPane'
import SpreadsheetNew from '../Spreadsheet'
import SpreadsheetSizer from '../../SpreadsheetTable/SpreadsheetSizer'

type SpreadsheetProps = React.ComponentProps<typeof SpreadsheetNew>
type SpreadsheetPaneProps = React.ComponentProps<typeof SpreadsheetPane>
type SearchBarProps = React.ComponentProps<typeof SearchBar>
type SwitchProps = React.ComponentProps<typeof Switch>

type SpreadsheetContainerProps = {
  /** This object includes the header and subHeader text */
  headerProps: {
    header: string
    subHeader: string
  }
  /** Generic SearchBar props */
  searchBarProps: SearchBarProps
  /** Generic Spreadsheet props */
  spreadsheetProps: SpreadsheetProps
  /** Optional - generic SpreadsheetPane props */
  spreadsheetPaneProps?: SpreadsheetPaneProps
  /** Optional - If the table is next to another element, the computed width of the table will be incorrect. Use this to input the width of the element (including padding, margin, and other spacing) to have the correct width for the table. */
  widthOffset?: number
  /** Optional Toggle Props */
  toggleProps?: {
    label: string
    toggleProps: SwitchProps
  }
  /** Optional prop to add a test id to the SpreadsheetContainer for QA testing */
  qaTestId?: string
}

// NOTE: This is an optional container for the Spreadsheet component. It is not necessary to be used for every Spreadsheet implementation.
const SpreadsheetContainer = ({
  headerProps,
  searchBarProps,
  spreadsheetPaneProps,
  spreadsheetProps,
  widthOffset,
  toggleProps,
  qaTestId = 'spreadsheet-container',
}: SpreadsheetContainerProps): React.JSX.Element => {
  const [collapsed, setCollapsed] = useState(false),
    spreadsheetPaneClass = spreadsheetPaneProps
      ? styles.withSpreadsheetPane
      : '',
    [showGradient, setShowGradient] = useState(false),
    headerRef = useRef<HTMLDivElement>(null)

  return (
    <SpreadsheetSizer
      {...(widthOffset
        ? {
            widthOffset: widthOffset,
          }
        : {})}
      qaTestId={qaTestId}
    >
      {({ width, height }) => {
        const spreadsheetWidth = spreadsheetPaneProps
            ? width -
              (collapsed
                ? 56 /** 56px is the width of the collapsed SpreadsheetPane */
                : 400) /** 400px is the width of the expanded SpreadsheetPane */
            : width,
          spreadsheetHeight = height - (headerRef.current?.clientHeight ?? 0),
          isContainerWithToggle = toggleProps

        return (
          <div
            className={`${styles.spreadsheetContainer} ${spreadsheetPaneClass}`}
            style={{
              width: width <= 0 ? '100%' : width,
              height: height <= 0 ? '100%' : height,
            }}
          >
            <div className={styles.leftSection}>
              <div className={styles.headerContainer} ref={headerRef}>
                <div>
                  <span className={styles.subHeader}>
                    {headerProps.subHeader}
                  </span>
                  <span className={styles.header}>{headerProps.header}</span>
                </div>
                <div className={styles.divider} />
                <div
                  className={`${isContainerWithToggle ? styles.searchBarContainerWithToggle : styles.searchBarContainer}`}
                >
                  <div
                    className={`${isContainerWithToggle ? styles.searchWrapper : ''}`}
                  >
                    <SearchBar {...searchBarProps} />
                  </div>
                  {isContainerWithToggle ? (
                    <div className={styles.toggleContainer}>
                      <Switch {...toggleProps.toggleProps} />
                      <span>{toggleProps.label}</span>
                    </div>
                  ) : null}
                </div>
              </div>
              <div>
                <SpreadsheetNew
                  {...spreadsheetProps}
                  {...(searchBarProps.value
                    ? { searchTerm: searchBarProps.value }
                    : {})}
                  customWidth={spreadsheetWidth}
                  customHeight={spreadsheetHeight}
                  onStickyTableClassChange={(stickyTableClass) => {
                    setShowGradient(stickyTableClass)
                  }}
                  noOutsideBorder
                />
                <div
                  className={`${styles.rightGradient} ${
                    showGradient && !spreadsheetProps.stickyTableConfig?.right
                      ? styles.showGradient
                      : ''
                  }`}
                />
              </div>
            </div>
            {spreadsheetPaneProps ? (
              <div className={styles.rightSection}>
                <SpreadsheetPane
                  {...spreadsheetPaneProps}
                  collapsed={collapsed}
                  setCollapsed={setCollapsed}
                />
              </div>
            ) : null}
          </div>
        )
      }}
    </SpreadsheetSizer>
  )
}

export default SpreadsheetContainer
